import type { PremiumInsight } from '../../../hooks/useReports';

interface Props {
  insights: PremiumInsight[];
  loading: boolean;
}

const typeConfig: Record<PremiumInsight['type'], { bg: string; border: string; text: string; iconColor: string }> = {
  positive: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', iconColor: 'text-emerald-500' },
  negative: { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     iconColor: 'text-red-500' },
  warning:  { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   iconColor: 'text-amber-500' },
  neutral:  { bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-700',    iconColor: 'text-gray-500' },
  danger:   { bg: 'bg-red-50',     border: 'border-red-300',     text: 'text-red-800',     iconColor: 'text-red-600' },
};

export const PremiumInsightsPanel = ({ insights, loading }: Props) => {
  if (loading) return (
    <div className="space-y-2">
      {[1,2,3,4].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
    </div>
  );

  if (!insights.length) return null;

  const dangers  = insights.filter(i => i.type === 'danger');
  const warnings = insights.filter(i => i.type === 'warning' || i.type === 'negative');
  const positives = insights.filter(i => i.type === 'positive');
  const neutrals  = insights.filter(i => i.type === 'neutral');

  const ordered = [...dangers, ...warnings, ...positives, ...neutrals];

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-xl shrink-0">
          <i className="ri-sparkling-line text-emerald-500 text-[17px]" />
        </div>
        <div>
          <h3 className="text-[15px] font-700 text-gray-900">Insights Premium</h3>
          <p className="text-[12px] text-gray-400">Análises automáticas baseadas nos seus dados financeiros</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {ordered.map((ins, i) => {
          const cfg = typeConfig[ins.type];
          return (
            <div
              key={i}
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${cfg.bg} ${cfg.border}`}
            >
              <div className={`w-7 h-7 flex items-center justify-center rounded-xl bg-white/60 shrink-0 mt-0.5`}>
                <i className={`${ins.icon} text-[15px] ${cfg.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[12px] font-700 ${cfg.text}`}>{ins.title}</p>
                <p className={`text-[11px] mt-0.5 ${cfg.text} opacity-80 leading-relaxed`}>{ins.text}</p>
              </div>
              {ins.value && (
                <span className={`text-[13px] font-800 ${cfg.text} whitespace-nowrap shrink-0`}>{ins.value}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
